/**
 * Landing Page - v2 Home Page with 4-Layer Architecture
 *
 * Demonstrates the orthogonal 4-layer system:
 * - Layer 1: LayoutTheme (HOW to display - component structure)
 * - Layer 2: DesignTokenSet (VISUAL - colors, fonts, spacing)
 * - Layer 3: ViewerProfile (WHAT to show - content filtering)
 * - Layer 4: RolePersona (WHO evaluates - power dynamics)
 */

import { useEffect, useRef } from 'react'
import { useLayoutTheme } from '@/contexts/LayoutThemeContext'
import { useDesignToken } from '@/contexts/DesignTokenContext'
import { useViewerProfile } from '@/contexts/ViewerProfileContext'
import { useRolePersona } from '@/contexts/RolePersonaContext'
import { useAuth } from '@/contexts/AuthContext'
import { Container } from '@/components/ui/Container'
import { GatedContentSection } from '@/components/access'
import { useSearchParams } from 'react-router-dom'
import { Card } from '@/components/ui/Card'
import { MetricCard } from '@/components/ui/MetricCard'
import { usePortfolioHighlights, usePerspectiveView } from '@/hooks/useGitHubEnrichment'

// Mac Studio Theme Components
import {
  MacStudioLayout,
  MacStudioWindow,
  TieredContent,
} from '@/components/mac-studio'

// Finder-style Window Components
import {
  PortfolioFinderWindow,
  TechStackWindow,
  StatsWindow,
} from '@/components/mac-studio/finder'

// Generated content
import { stats, career } from '@/generated/content'

// Declare Vanta type for TypeScript
declare global {
  interface Window {
    VANTA: any;
    THREE: any;
  }
}

export default function LandingPage() {
  const { themeConfig } = useLayoutTheme()
  const { tokenConfig } = useDesignToken()
  const { currentProfile } = useViewerProfile()
  const { currentPersona } = useRolePersona()
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  const accessToken = searchParams.get('token') || undefined

  // Vanta.js effect reference
  const vantaRef = useRef<HTMLDivElement>(null)
  const vantaEffect = useRef<any>(null)

  // GitHub-enriched portfolio intelligence
  const portfolioHighlights = usePortfolioHighlights()
  const { view: perspectiveView } = usePerspectiveView()

  // Initialize Vanta Birds background for modern-cards layout
  useEffect(() => {
    if (themeConfig.id === 'modern-cards' && vantaRef.current && !vantaEffect.current) {
      // Wait for Vanta to be available
      if (window.VANTA && window.THREE) {
        vantaEffect.current = window.VANTA.BIRDS({
          el: vantaRef.current,
          mouseControls: true,
          touchControls: true,
          gyroControls: false,
          minHeight: 200.00,
          minWidth: 200.00,
          scale: 1.00,
          scaleMobile: 1.00,
          backgroundColor: 0xffffff,
          color1: 0x707070,
          color2: 0xffffff,
          colorMode: 'lerp',
          wingSpan: 15.00,
          speedLimit: 4.00,
          separation: 68.00,
          alignment: 8.00,
          cohesion: 32.00,
          quantity: 4.00,
          backgroundAlpha: 0.31
        })
      }
    }

    // Cleanup function
    return () => {
      if (vantaEffect.current) {
        vantaEffect.current.destroy()
        vantaEffect.current = null
      }
    }
  }, [themeConfig.id])

  // Content adapts based on ViewerProfile (Layer 3)
  const getHeadline = () => {
    switch (currentProfile) {
      case 'recruiter-view':
        return 'Platform Engineering for Lean Teams | 30 Years Seattle → Zurich'
      case 'technical-view':
        return 'AI-Native Architectures + Edge Computing + Cost Optimization at Scale'
      case 'executive-view':
        return '10x Cost Reductions, 5x Team Multipliers | Habitusnet Consulting AG'
      default:
        return 'Platform Engineering for Lean Teams'
    }
  }

  const getDescription = () => {
    switch (currentProfile) {
      case 'recruiter-view':
        return '30 years synthesizing American innovation velocity with Swiss operational rigor. Founder of Habitusnet Consulting AG in Zurich. Standard deliverable: 10x cost reductions while improving performance. Managing 30-40 AI projects simultaneously, building systems that compound in value.'
      case 'technical-view':
        return 'Boring technology at the edge: Proven foundations + cutting-edge distribution. Cloudflare Workers for global deployment ($3-5/month production). MCP-based tool orchestration where components amplify each other\'s capabilities. AI as infrastructure, not feature.'
      case 'executive-view':
        return 'Building infrastructure enabling small teams to accomplish enterprise-scale operations. One person with Beautiful Invoicing handles work requiring entire AR departments. 5-person teams manage infrastructure typically requiring dedicated DevOps teams. 10x cost reductions standard.'
      default:
        return 'Building systems that enable 5-person teams to accomplish enterprise-scale operations through intelligent automation and platform engineering. 30-year journey bridging Seattle\'s innovation culture and Swiss operational excellence.'
    }
  }

  // Layout adapts based on LayoutTheme (Layer 1)
  if (themeConfig.id === 'modern-cards') {
    return (
      <div ref={vantaRef} style={{
        minHeight: '100vh',
        backgroundColor: 'var(--color-background)',
        fontFamily: 'var(--font-sans)',
      }}>
        <Container size="xl">
          <div style={{ padding: 'var(--spacing-2xl) 0' }}>
            {/* Hero Section */}
            <header style={{
              textAlign: 'center',
              marginBottom: 'var(--spacing-4xl)',
              paddingTop: 'var(--spacing-2xl)',
            }}>
              <h1 style={{
                fontSize: 'clamp(2rem, 8vw, 3.5rem)',
                fontWeight: 700,
                color: 'var(--color-text-primary)',
                marginBottom: 'var(--spacing-md)',
                lineHeight: 1.2,
              }}>
                Agustin Musi
              </h1>
              <p style={{
                fontSize: 'clamp(1.125rem, 4vw, 1.5rem)',
                color: 'var(--color-text-secondary)',
                marginBottom: 'var(--spacing-xl)',
                padding: '0 var(--spacing-md)',
              }}>
                {getHeadline()}
              </p>
              <p style={{
                fontSize: 'clamp(1rem, 3vw, 1.125rem)',
                color: 'var(--color-text-secondary)',
                maxWidth: '700px',
                margin: '0 auto',
                lineHeight: 1.8,
                padding: '0 var(--spacing-md)',
              }}>
                {getDescription()}
              </p>
            </header>

            {/* UX3: Prominent Metrics - Von Restorff Effect */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 'var(--spacing-lg)',
              marginBottom: 'var(--spacing-4xl)',
              padding: '0 var(--spacing-md)',
            }}>
              <MetricCard
                metric="10x"
                label="Cost Reductions"
                description="Standard deliverable across all engagements"
                emphasis="accent"
              />
              <MetricCard
                metric="5x"
                label="Team Multipliers"
                description="5-person teams achieving 50-person capabilities"
                emphasis="primary"
              />
              <MetricCard
                metric="30-40"
                label="AI Projects"
                description="Managed simultaneously with compounding value"
                emphasis="secondary"
              />
            </div>

            {/* UX13: Clear CTA Hierarchy - Von Restorff Effect for Conversion */}
            <div style={{
              textAlign: 'center',
              marginBottom: 'var(--spacing-4xl)',
              padding: '0 var(--spacing-md)',
              display: 'flex',
              gap: 'var(--spacing-md)',
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}>
              {/* PRIMARY CTA - Most prominent */}
              <a href="/projects" className="cta-primary">
                Explore Projects →
              </a>

              {/* SECONDARY CTA - Less prominent */}
              <a href="/background" className="cta-secondary">
                Learn More About Me
              </a>
            </div>

            {/* Key Differentiators - Core Value Propositions */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: 'var(--spacing-xl)',
              marginBottom: 'var(--spacing-4xl)',
            }}>
              {/* Compounding Systems */}
              <div style={{
                padding: 'var(--spacing-xl)',
                backgroundColor: 'var(--color-surface)',
                borderRadius: 'var(--radius-lg)',
                border: '1px solid rgba(0,0,0,0.05)',
              }}>
                <div style={{
                  fontSize: '2.5rem',
                  marginBottom: 'var(--spacing-md)',
                }}>
                  📈
                </div>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  color: 'var(--color-text-primary)',
                  marginBottom: 'var(--spacing-sm)',
                }}>
                  Compounding Systems
                </h3>
                <p style={{
                  color: 'var(--color-text-secondary)',
                  fontSize: '0.9375rem',
                  lineHeight: 1.8,
                }}>
                  Building platforms that appreciate in value. Managing 30-40 AI projects simultaneously,
                  getting smarter about dependency resolution with each deployment. Systems that become
                  more valuable as they're used.
                </p>
              </div>

              {/* Lean Team Enablement */}
              <div style={{
                padding: 'var(--spacing-xl)',
                backgroundColor: 'var(--color-surface)',
                borderRadius: 'var(--radius-lg)',
                border: '1px solid rgba(0,0,0,0.05)',
              }}>
                <div style={{
                  fontSize: '2.5rem',
                  marginBottom: 'var(--spacing-md)',
                }}>
                  👥✕
                </div>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  color: 'var(--color-text-primary)',
                  marginBottom: 'var(--spacing-sm)',
                }}>
                  Lean Team Enablement
                </h3>
                <p style={{
                  color: 'var(--color-text-secondary)',
                  fontSize: '0.9375rem',
                  lineHeight: 1.8,
                }}>
                  One person with Beautiful Invoicing handles work requiring entire AR departments.
                  5-person teams manage infrastructure typically requiring dedicated DevOps teams.
                  Where 5-person teams do 50-person work.
                </p>
              </div>

              {/* Bridging Worlds */}
              <div style={{
                padding: 'var(--spacing-xl)',
                backgroundColor: 'var(--color-surface)',
                borderRadius: 'var(--radius-lg)',
                border: '1px solid rgba(0,0,0,0.05)',
              }}>
                <div style={{
                  fontSize: '2.5rem',
                  marginBottom: 'var(--spacing-md)',
                }}>
                  🌉
                </div>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  color: 'var(--color-text-primary)',
                  marginBottom: 'var(--spacing-sm)',
                }}>
                  Bridging Worlds
                </h3>
                <p style={{
                  color: 'var(--color-text-secondary)',
                  fontSize: '0.9375rem',
                  lineHeight: 1.8,
                }}>
                  30-year Seattle → Zurich journey. Synthesis of American velocity with Swiss reliability.
                  Cutting-edge AI with proven technology. "Boring technology at the edge" - proven foundations,
                  cutting-edge distribution.
                </p>
              </div>
            </div>

            {/* Featured Projects Portfolio */}
            <div style={{
              marginBottom: 'var(--spacing-4xl)',
            }}>
              <h2 style={{
                fontSize: 'clamp(1.75rem, 5vw, 2.25rem)',
                fontWeight: 700,
                color: 'var(--color-text-primary)',
                marginBottom: 'var(--spacing-sm)',
                textAlign: 'center',
              }}>
                Selected Projects
              </h2>
              <p style={{
                fontSize: 'clamp(1rem, 3vw, 1.125rem)',
                color: 'var(--color-text-secondary)',
                marginBottom: 'var(--spacing-2xl)',
                textAlign: 'center',
              }}>
                Each project solves coordination problems at different scales
              </p>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                gap: 'var(--spacing-xl)',
              }}>
                {/* Vibe Coding Management Platform */}
                <div style={{
                  padding: 'var(--spacing-xl)',
                  backgroundColor: 'var(--color-surface)',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid rgba(0,0,0,0.05)',
                }}>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    color: 'var(--color-text-primary)',
                    marginBottom: 'var(--spacing-xs)',
                  }}>
                    🎯 Vibe Coding Management Platform
                  </h3>
                  <p style={{
                    fontSize: '0.875rem',
                    color: 'var(--color-primary)',
                    fontWeight: 600,
                    marginBottom: 'var(--spacing-sm)',
                  }}>
                    DevOps for AI-Generated Code
                  </p>
                  <p style={{
                    color: 'var(--color-text-secondary)',
                    fontSize: '0.9375rem',
                    lineHeight: 1.8,
                    marginBottom: 'var(--spacing-md)',
                  }}>
                    Managing 30-40 lovable.dev projects simultaneously. New paradigms for dependency
                    management in AI code era. Platform engineering + AI integration + DevOps automation.
                  </p>
                  <div style={{
                    fontSize: '0.875rem',
                    color: 'var(--color-text-secondary)',
                  }}>
                    <strong>Outcome:</strong> [Request access to view metrics]
                  </div>
                </div>

                {/* Beautiful Invoicing */}
                <div style={{
                  padding: 'var(--spacing-xl)',
                  backgroundColor: 'var(--color-surface)',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid rgba(0,0,0,0.05)',
                }}>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    color: 'var(--color-text-primary)',
                    marginBottom: 'var(--spacing-xs)',
                  }}>
                    💼 Beautiful Invoicing (Wixell)
                  </h3>
                  <p style={{
                    fontSize: '0.875rem',
                    color: 'var(--color-primary)',
                    fontWeight: 600,
                    marginBottom: 'var(--spacing-sm)',
                  }}>
                    One Person, Entire AR Department
                  </p>
                  <p style={{
                    color: 'var(--color-text-secondary)',
                    fontSize: '0.9375rem',
                    lineHeight: 1.8,
                    marginBottom: 'var(--spacing-md)',
                  }}>
                    Eliminate friction, preserve human judgment. Automation as amplification, not replacement.
                    Business automation + workflow optimization.
                  </p>
                  <div style={{
                    fontSize: '0.875rem',
                    color: 'var(--color-text-secondary)',
                  }}>
                    <strong>Impact:</strong> [Request access to view metrics]
                  </div>
                </div>

                {/* No More CV */}
                <div style={{
                  padding: 'var(--spacing-xl)',
                  backgroundColor: 'var(--color-surface)',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid rgba(0,0,0,0.05)',
                }}>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    color: 'var(--color-text-primary)',
                    marginBottom: 'var(--spacing-xs)',
                  }}>
                    📋 No More CV
                  </h3>
                  <p style={{
                    fontSize: '0.875rem',
                    color: 'var(--color-primary)',
                    fontWeight: 600,
                    marginBottom: 'var(--spacing-sm)',
                  }}>
                    Audience-Focused Career Platforms
                  </p>
                  <p style={{
                    color: 'var(--color-text-secondary)',
                    fontSize: '0.9375rem',
                    lineHeight: 1.8,
                    marginBottom: 'var(--spacing-md)',
                  }}>
                    Contrarian rethinking of recruitment industry. Interactive perspectives vs. static PDFs.
                    UX innovation + platform design.
                  </p>
                  <div style={{
                    fontSize: '0.875rem',
                    color: 'var(--color-text-secondary)',
                  }}>
                    <strong>Innovation:</strong> You're experiencing it now
                  </div>
                </div>

                {/* Platform Engineering Nexus */}
                <div style={{
                  padding: 'var(--spacing-xl)',
                  backgroundColor: 'var(--color-surface)',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid rgba(0,0,0,0.05)',
                }}>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    color: 'var(--color-text-primary)',
                    marginBottom: 'var(--spacing-xs)',
                  }}>
                    🔗 Platform Engineering Nexus
                  </h3>
                  <p style={{
                    fontSize: '0.875rem',
                    color: 'var(--color-primary)',
                    fontWeight: 600,
                    marginBottom: 'var(--spacing-sm)',
                  }}>
                    Tool Orchestration Ecosystem
                  </p>
                  <p style={{
                    color: 'var(--color-text-secondary)',
                    fontSize: '0.9375rem',
                    lineHeight: 1.8,
                    marginBottom: 'var(--spacing-md)',
                  }}>
                    Components amplify each other's value. MCP + system integration + ecosystem design.
                    Value through connections.
                  </p>
                  <div style={{
                    fontSize: '0.875rem',
                    color: 'var(--color-text-secondary)',
                  }}>
                    <strong>Architecture:</strong> [Request access to view details]
                  </div>
                </div>

                {/* Vouch4U */}
                <div style={{
                  padding: 'var(--spacing-xl)',
                  backgroundColor: 'var(--color-surface)',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid rgba(0,0,0,0.05)',
                }}>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    color: 'var(--color-text-primary)',
                    marginBottom: 'var(--spacing-xs)',
                  }}>
                    🤝 Vouch4U
                  </h3>
                  <p style={{
                    fontSize: '0.875rem',
                    color: 'var(--color-primary)',
                    fontWeight: 600,
                    marginBottom: 'var(--spacing-sm)',
                  }}>
                    Decentralized Reputation Systems
                  </p>
                  <p style={{
                    color: 'var(--color-text-secondary)',
                    fontSize: '0.9375rem',
                    lineHeight: 1.8,
                    marginBottom: 'var(--spacing-md)',
                  }}>
                    Trust systems for digital environments. Reputation as infrastructure. Solving coordination
                    problems at scale.
                  </p>
                  <div style={{
                    fontSize: '0.875rem',
                    color: 'var(--color-text-secondary)',
                  }}>
                    <strong>Innovation:</strong> [Request access to view details]
                  </div>
                </div>

                {/* Additional Projects */}
                <div style={{
                  padding: 'var(--spacing-xl)',
                  backgroundColor: 'var(--color-surface)',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid rgba(0,0,0,0.05)',
                }}>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    color: 'var(--color-text-primary)',
                    marginBottom: 'var(--spacing-xs)',
                  }}>
                    ⚡ Additional Platforms
                  </h3>
                  <p style={{
                    fontSize: '0.875rem',
                    color: 'var(--color-primary)',
                    fontWeight: 600,
                    marginBottom: 'var(--spacing-sm)',
                  }}>
                    15+ Distinct Tools & Platforms
                  </p>
                  <p style={{
                    color: 'var(--color-text-secondary)',
                    fontSize: '0.9375rem',
                    lineHeight: 1.8,
                    marginBottom: 'var(--spacing-md)',
                  }}>
                    Rhelvia, Power Insights, SyncUp, DevTools Extensions, and more. Each solving specific
                    coordination challenges.
                  </p>
                  <a href="/portfolio-intelligence" style={{
                    color: 'var(--color-primary)',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    textDecoration: 'none',
                  }}>
                    Request full portfolio access →
                  </a>
                </div>
              </div>
            </div>

            {/* Professional Overview - Tiered Content */}
            <GatedContentSection
              publicContent={
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                  gap: 'var(--spacing-md)',
                  marginBottom: 'var(--spacing-2xl)',
                }}>
                  {/* Quick Links */}
                  <a href="/background" style={{
                    padding: 'var(--spacing-md)',
                    backgroundColor: 'var(--color-surface)',
                    borderRadius: 'var(--radius-md)',
                    borderLeft: '3px solid var(--color-primary)',
                    textDecoration: 'none',
                    transition: 'all 0.2s',
                  }}>
                    <h3 style={{
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: 'var(--color-text-primary)',
                      marginBottom: 'var(--spacing-xs)',
                    }}>
                      📋 Background
                    </h3>
                    <p style={{
                      color: 'var(--color-text-secondary)',
                      fontSize: '0.875rem',
                      lineHeight: 1.6,
                    }}>
                      Professional journey & expertise
                    </p>
                  </a>

                  <a href="/interests" style={{
                    padding: 'var(--spacing-md)',
                    backgroundColor: 'var(--color-surface)',
                    borderRadius: 'var(--radius-md)',
                    borderLeft: '3px solid var(--color-primary)',
                    textDecoration: 'none',
                    transition: 'all 0.2s',
                  }}>
                    <h3 style={{
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: 'var(--color-text-primary)',
                      marginBottom: 'var(--spacing-xs)',
                    }}>
                      🎯 Interests
                    </h3>
                    <p style={{
                      color: 'var(--color-text-secondary)',
                      fontSize: '0.875rem',
                      lineHeight: 1.6,
                    }}>
                      Research focus & passions
                    </p>
                  </a>

                  <a href="/writings" style={{
                    padding: 'var(--spacing-md)',
                    backgroundColor: 'var(--color-surface)',
                    borderRadius: 'var(--radius-md)',
                    borderLeft: '3px solid var(--color-primary)',
                    textDecoration: 'none',
                    transition: 'all 0.2s',
                  }}>
                    <h3 style={{
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: 'var(--color-text-primary)',
                      marginBottom: 'var(--spacing-xs)',
                    }}>
                      ✍️ Writings
                    </h3>
                    <p style={{
                      color: 'var(--color-text-secondary)',
                      fontSize: '0.875rem',
                      lineHeight: 1.6,
                    }}>
                      Publications & thought leadership
                    </p>
                  </a>

                  <a href="/projects" style={{
                    padding: 'var(--spacing-md)',
                    backgroundColor: 'var(--color-surface)',
                    borderRadius: 'var(--radius-md)',
                    borderLeft: '3px solid var(--color-primary)',
                    textDecoration: 'none',
                    transition: 'all 0.2s',
                  }}>
                    <h3 style={{
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: 'var(--color-text-primary)',
                      marginBottom: 'var(--spacing-xs)',
                    }}>
                      🚀 Projects
                    </h3>
                    <p style={{
                      color: 'var(--color-text-secondary)',
                      fontSize: '0.875rem',
                      lineHeight: 1.6,
                    }}>
                      Portfolio & implementations
                    </p>
                  </a>
                </div>
              }
              gatedContent={
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
                  gap: 'var(--spacing-xl)',
                  marginBottom: 'var(--spacing-2xl)',
                }}>
                  {/* Perspective-Specific Content */}
                  {currentProfile === 'recruiter-view' && perspectiveView && 'summary' in perspectiveView && (
                    <>
                      {/* Professional Summary */}
                      <Card padding="lg">
                        <h3 style={{
                          fontSize: '1.25rem',
                          fontWeight: 600,
                          color: 'var(--color-text-primary)',
                          marginBottom: 'var(--spacing-md)',
                        }}>
                          📋 Professional Summary
                        </h3>
                        <div style={{
                          color: 'var(--color-text-secondary)',
                          fontSize: '0.9375rem',
                          lineHeight: 1.8,
                          marginBottom: 'var(--spacing-md)',
                        }}>
                          {perspectiveView.summary}
                        </div>
                        <a href="/background" style={{
                          color: 'var(--color-primary)',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          textDecoration: 'none',
                        }}>
                          View full background →
                        </a>
                      </Card>

                      {/* Key Strengths */}
                      <Card padding="lg">
                        <h3 style={{
                          fontSize: '1.25rem',
                          fontWeight: 600,
                          color: 'var(--color-text-primary)',
                          marginBottom: 'var(--spacing-md)',
                        }}>
                          💪 Key Strengths
                        </h3>
                        <ul style={{
                          listStyle: 'none',
                          paddingLeft: 0,
                          display: 'grid',
                          gap: 'var(--spacing-sm)',
                          color: 'var(--color-text-secondary)',
                          fontSize: '0.9375rem',
                          lineHeight: 1.8,
                        }}>
                          {perspectiveView.keyStrengths.map((strength, i) => (
                            <li key={i}>✅ {strength}</li>
                          ))}
                        </ul>
                      </Card>

                      {/* Impact Metrics */}
                      <Card padding="lg">
                        <h3 style={{
                          fontSize: '1.25rem',
                          fontWeight: 600,
                          color: 'var(--color-text-primary)',
                          marginBottom: 'var(--spacing-md)',
                        }}>
                          📊 Impact Metrics
                        </h3>
                        <div style={{
                          display: 'grid',
                          gap: 'var(--spacing-sm)',
                          color: 'var(--color-text-secondary)',
                          fontSize: '0.9375rem',
                          lineHeight: 1.8,
                        }}>
                          {perspectiveView.impactMetrics.map((metric, i) => (
                            <div key={i}><strong>{metric.label}:</strong> {metric.value}</div>
                          ))}
                        </div>
                      </Card>

                      {/* Standout Projects */}
                      <Card padding="lg">
                        <h3 style={{
                          fontSize: '1.25rem',
                          fontWeight: 600,
                          color: 'var(--color-text-primary)',
                          marginBottom: 'var(--spacing-md)',
                        }}>
                          🚀 Standout Projects
                        </h3>
                        <div style={{
                          display: 'grid',
                          gap: 'var(--spacing-md)',
                          color: 'var(--color-text-secondary)',
                          fontSize: '0.9375rem',
                          lineHeight: 1.8,
                        }}>
                          {perspectiveView.standoutProjects.slice(0, 3).map((project, i) => (
                            <div key={i}>
                              <div style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>{project.name}</div>
                              <div style={{ fontSize: '0.875rem' }}>{project.impact}</div>
                            </div>
                          ))}
                        </div>
                        <a href="/portfolio-intelligence" style={{
                          color: 'var(--color-primary)',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          textDecoration: 'none',
                          display: 'block',
                          marginTop: 'var(--spacing-md)',
                        }}>
                          View all projects →
                        </a>
                      </Card>
                    </>
                  )}

                  {currentProfile === 'technical-view' && perspectiveView && 'architectureExpertise' in perspectiveView && (
                    <>
                      {/* Architecture Expertise */}
                      <Card padding="lg">
                        <h3 style={{
                          fontSize: '1.25rem',
                          fontWeight: 600,
                          color: 'var(--color-text-primary)',
                          marginBottom: 'var(--spacing-md)',
                        }}>
                          🏗️ Architecture Expertise
                        </h3>
                        <ul style={{
                          listStyle: 'none',
                          paddingLeft: 0,
                          display: 'grid',
                          gap: 'var(--spacing-sm)',
                          color: 'var(--color-text-secondary)',
                          fontSize: '0.9375rem',
                          lineHeight: 1.8,
                        }}>
                          {perspectiveView.architectureExpertise.map((item, i) => (
                            <li key={i}>• {item}</li>
                          ))}
                        </ul>
                      </Card>

                      {/* Code Quality Metrics */}
                      <Card padding="lg">
                        <h3 style={{
                          fontSize: '1.25rem',
                          fontWeight: 600,
                          color: 'var(--color-text-primary)',
                          marginBottom: 'var(--spacing-md)',
                        }}>
                          📊 Code Quality Metrics
                        </h3>
                        <div style={{
                          display: 'grid',
                          gap: 'var(--spacing-sm)',
                          color: 'var(--color-text-secondary)',
                          fontSize: '0.9375rem',
                          lineHeight: 1.8,
                        }}>
                          {perspectiveView.codeQualityMetrics.map((metric, i) => (
                            <div key={i}><strong>{metric.metric}:</strong> {metric.score}</div>
                          ))}
                        </div>
                      </Card>

                      {/* Technical Decisions */}
                      <Card padding="lg">
                        <h3 style={{
                          fontSize: '1.25rem',
                          fontWeight: 600,
                          color: 'var(--color-text-primary)',
                          marginBottom: 'var(--spacing-md)',
                        }}>
                          🎯 Technical Decisions
                        </h3>
                        <div style={{
                          display: 'grid',
                          gap: 'var(--spacing-md)',
                          color: 'var(--color-text-secondary)',
                          fontSize: '0.9375rem',
                          lineHeight: 1.8,
                        }}>
                          {perspectiveView.technicalDecisions.slice(0, 3).map((decision, i) => (
                            <div key={i}>
                              <div style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>{decision.decision}</div>
                              <div style={{ fontSize: '0.875rem' }}>{decision.rationale}</div>
                            </div>
                          ))}
                        </div>
                      </Card>

                      {/* Standout Implementations */}
                      <Card padding="lg">
                        <h3 style={{
                          fontSize: '1.25rem',
                          fontWeight: 600,
                          color: 'var(--color-text-primary)',
                          marginBottom: 'var(--spacing-md)',
                        }}>
                          ⭐ Standout Implementations
                        </h3>
                        <div style={{
                          display: 'grid',
                          gap: 'var(--spacing-md)',
                          color: 'var(--color-text-secondary)',
                          fontSize: '0.9375rem',
                          lineHeight: 1.8,
                        }}>
                          {perspectiveView.standoutImplementations.slice(0, 3).map((impl, i) => (
                            <div key={i}>
                              <div style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>{impl.name}</div>
                              <div style={{ fontSize: '0.875rem' }}>{impl.solution}</div>
                            </div>
                          ))}
                        </div>
                        <a href="/portfolio-intelligence" style={{
                          color: 'var(--color-primary)',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          textDecoration: 'none',
                          display: 'block',
                          marginTop: 'var(--spacing-md)',
                        }}>
                          View technical deep-dive →
                        </a>
                      </Card>
                    </>
                  )}

                  {currentProfile === 'executive-view' && perspectiveView && 'strategicValue' in perspectiveView && (
                    <>
                      {/* Strategic Value */}
                      <Card padding="lg">
                        <h3 style={{
                          fontSize: '1.25rem',
                          fontWeight: 600,
                          color: 'var(--color-text-primary)',
                          marginBottom: 'var(--spacing-md)',
                        }}>
                          🎯 Strategic Value
                        </h3>
                        <div style={{
                          color: 'var(--color-text-secondary)',
                          fontSize: '0.9375rem',
                          lineHeight: 1.8,
                        }}>
                          {perspectiveView.strategicValue}
                        </div>
                      </Card>

                      {/* Cost Optimizations */}
                      <Card padding="lg">
                        <h3 style={{
                          fontSize: '1.25rem',
                          fontWeight: 600,
                          color: 'var(--color-text-primary)',
                          marginBottom: 'var(--spacing-md)',
                        }}>
                          💰 Cost Optimizations
                        </h3>
                        <ul style={{
                          listStyle: 'none',
                          paddingLeft: 0,
                          display: 'grid',
                          gap: 'var(--spacing-sm)',
                          color: 'var(--color-text-secondary)',
                          fontSize: '0.9375rem',
                          lineHeight: 1.8,
                        }}>
                          {perspectiveView.costOptimizations.map((opt: any, i: number) => (
                            <li key={i}>💵 {typeof opt === 'string' ? opt : opt.initiative}</li>
                          ))}
                        </ul>
                      </Card>

                      {/* Scalability Achievements */}
                      <Card padding="lg">
                        <h3 style={{
                          fontSize: '1.25rem',
                          fontWeight: 600,
                          color: 'var(--color-text-primary)',
                          marginBottom: 'var(--spacing-md)',
                        }}>
                          📈 Scalability Achievements
                        </h3>
                        <ul style={{
                          listStyle: 'none',
                          paddingLeft: 0,
                          display: 'grid',
                          gap: 'var(--spacing-sm)',
                          color: 'var(--color-text-secondary)',
                          fontSize: '0.9375rem',
                          lineHeight: 1.8,
                        }}>
                          {perspectiveView.scalabilityAchievements.map((achievement, i) => (
                            <li key={i}>📊 {achievement}</li>
                          ))}
                        </ul>
                      </Card>

                      {/* Business Outcomes */}
                      <Card padding="lg">
                        <h3 style={{
                          fontSize: '1.25rem',
                          fontWeight: 600,
                          color: 'var(--color-text-primary)',
                          marginBottom: 'var(--spacing-md)',
                        }}>
                          🎯 Business Outcomes
                        </h3>
                        <div style={{
                          display: 'grid',
                          gap: 'var(--spacing-md)',
                          color: 'var(--color-text-secondary)',
                          fontSize: '0.9375rem',
                          lineHeight: 1.8,
                        }}>
                          {perspectiveView.businessOutcomes.slice(0, 3).map((outcome, i) => (
                            <div key={i}>
                              <div style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>{outcome.outcome}</div>
                              <div style={{ fontSize: '0.875rem' }}>{outcome.impact}</div>
                            </div>
                          ))}
                        </div>
                        <a href="/portfolio-intelligence" style={{
                          color: 'var(--color-primary)',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          textDecoration: 'none',
                          display: 'block',
                          marginTop: 'var(--spacing-md)',
                        }}>
                          View business impact analysis →
                        </a>
                      </Card>
                    </>
                  )}

                  {/* Public View or Fallback */}
                  {(!perspectiveView || currentProfile === 'public-view' || 'professionalSummary' in perspectiveView) && (
                    <>
                      {/* Professional Summary */}
                      <Card padding="lg">
                        <h3 style={{
                          fontSize: '1.25rem',
                          fontWeight: 600,
                          color: 'var(--color-text-primary)',
                          marginBottom: 'var(--spacing-md)',
                        }}>
                          📋 Professional Summary
                        </h3>
                        <div style={{
                          color: 'var(--color-text-secondary)',
                          fontSize: '0.9375rem',
                          lineHeight: 1.8,
                          marginBottom: 'var(--spacing-md)',
                        }}>
                          {perspectiveView && 'professionalSummary' in perspectiveView
                            ? perspectiveView.professionalSummary
                            : 'Full-stack engineer with expertise in React, TypeScript, and cloud-native architecture. Proven track record of delivering scalable applications with modern tech stacks.'}
                        </div>
                        <a href="/background" style={{
                          color: 'var(--color-primary)',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          textDecoration: 'none',
                        }}>
                          View full background →
                        </a>
                      </Card>

                      {/* Technical Expertise */}
                      <Card padding="lg">
                        <h3 style={{
                          fontSize: '1.25rem',
                          fontWeight: 600,
                          color: 'var(--color-text-primary)',
                          marginBottom: 'var(--spacing-md)',
                        }}>
                          🚀 Technical Expertise
                        </h3>
                        <div style={{
                          color: 'var(--color-text-secondary)',
                          fontSize: '0.9375rem',
                          lineHeight: 1.8,
                        }}>
                          <div style={{
                            display: 'grid',
                            gap: 'var(--spacing-sm)',
                            marginBottom: 'var(--spacing-md)',
                          }}>
                            {portfolioHighlights.technicalSkills.map((skill, i) => (
                              <div key={i}><strong>{skill.category}:</strong> {skill.skills.join(', ')}</div>
                            ))}
                          </div>
                        </div>
                        <a href="/projects" style={{
                          color: 'var(--color-primary)',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          textDecoration: 'none',
                        }}>
                          View projects →
                        </a>
                      </Card>

                      {/* Key Achievements */}
                      <Card padding="lg">
                        <h3 style={{
                          fontSize: '1.25rem',
                          fontWeight: 600,
                          color: 'var(--color-text-primary)',
                          marginBottom: 'var(--spacing-md)',
                        }}>
                          ⭐ Key Achievements
                        </h3>
                        <ul style={{
                          listStyle: 'none',
                          paddingLeft: 0,
                          display: 'grid',
                          gap: 'var(--spacing-sm)',
                          color: 'var(--color-text-secondary)',
                          fontSize: '0.9375rem',
                          lineHeight: 1.8,
                        }}>
                          {portfolioHighlights.achievements.map((achievement, i) => (
                            <li key={i}>✅ {achievement}</li>
                          ))}
                        </ul>
                      </Card>

                      {/* Top Projects */}
                      <Card padding="lg">
                        <h3 style={{
                          fontSize: '1.25rem',
                          fontWeight: 600,
                          color: 'var(--color-text-primary)',
                          marginBottom: 'var(--spacing-md)',
                        }}>
                          🌟 Featured Projects
                        </h3>
                        <div style={{
                          display: 'grid',
                          gap: 'var(--spacing-md)',
                          color: 'var(--color-text-secondary)',
                          fontSize: '0.9375rem',
                          lineHeight: 1.8,
                        }}>
                          {portfolioHighlights.topProjects.slice(0, 3).map((project: any, i: number) => (
                            <div key={i}>
                              <div style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
                                {project.name} ({project.stars}⭐)
                              </div>
                              <div style={{ fontSize: '0.875rem' }}>{project.description}</div>
                            </div>
                          ))}
                        </div>
                        <a href="/portfolio-intelligence" style={{
                          color: 'var(--color-primary)',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          textDecoration: 'none',
                          display: 'block',
                          marginTop: 'var(--spacing-md)',
                        }}>
                          View all projects →
                        </a>
                      </Card>
                    </>
                  )}
                </div>
              }
              contentType="portfolio-overview"
              contentLabel="Detailed Professional Profile"
              accessToken={accessToken}
            />

            {/* Admin Access (Only for authenticated users) */}
            {/* TERTIARY CTA - Least prominent (admin/sign-in) */}
            {user && (
              <div style={{
                textAlign: 'center',
                padding: 'var(--spacing-lg)',
                backgroundColor: 'rgba(var(--color-primary-rgb), 0.03)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid rgba(0,0,0,0.05)',
              }}>
                <p style={{
                  color: 'var(--color-text-secondary)',
                  marginBottom: 'var(--spacing-sm)',
                  fontSize: '0.875rem',
                }}>
                  Welcome back, {user.displayName || user.email}
                </p>
                <a href="/admin" className="cta-tertiary">
                  Dashboard →
                </a>
              </div>
            )}
          </div>
        </Container>
      </div>
    )
  }

  // Matrix Terminal Layout - Retro CLI-inspired interface
  if (themeConfig.id === 'matrix-terminal') {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#000',
        color: '#00ff00',
        fontFamily: '"Courier New", monospace',
        padding: 'var(--spacing-md)',
      }}>
        <Container size="lg">
          <div style={{ paddingTop: 'var(--spacing-lg)' }}>
            {/* Terminal Header */}
            <pre style={{
              fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
              lineHeight: 1.4,
              marginBottom: 'var(--spacing-xl)',
              whiteSpace: 'pre-wrap',
              overflowX: 'auto',
            }}>
{`┌─────────────────────────────────────────────────┐
│  AGUSTIN MUSI - HABITUSNET CONSULTING AG        │
│  Platform Engineering for Lean Teams            │
│  [${new Date().toISOString()}]          │
└─────────────────────────────────────────────────┘

> whoami
${getHeadline()}

> cat about.txt
${getDescription()}

> ls -la /projects/
drwxr-xr-x  6 agustin  staff  4096 Nov 12 2025 .
-rw-r--r--  1 agustin  staff  8192 Nov 12 2025 vibe-coding-platform.md
-rw-r--r--  1 agustin  staff  4096 Nov 12 2025 beautiful-invoicing.md
-rw-r--r--  1 agustin  staff  2048 Nov 12 2025 no-more-cv.md
-rw-r--r--  1 agustin  staff  2048 Nov 12 2025 platform-nexus.md
-rw-r--r--  1 agustin  staff  1024 Nov 12 2025 vouch4u.md

> cat /stats/value-delivered.txt
10x cost reductions (standard deliverable)
5x team multipliers (5-person teams → 50-person capabilities)
30-40 AI projects managed simultaneously
$3-5/month production deployments

> ./config.sh --show-layers
[Layer 1] LayoutTheme:     ${themeConfig.name}
[Layer 2] DesignTokenSet:  ${tokenConfig.culturalOrigin}
[Layer 3] ViewerProfile:   ${currentProfile}
[Layer 4] RolePersona:     ${currentPersona}

> echo "Philosophy:"
"Boring technology at the edge"
AI as infrastructure, not feature
Economic consciousness as design constraint
Systems that compound in value

> ${user ? `echo "Authenticated as: ${user.displayName || user.email}"` : 'echo "Guest session - Sign in available"'}
${user ? '> logout' : '> login'}
`}
            </pre>

            {/* Terminal Commands */}
            <div style={{
              border: '1px solid #00ff00',
              padding: 'var(--spacing-md)',
              marginBottom: 'var(--spacing-lg)',
            }}>
              <div style={{ marginBottom: 'var(--spacing-sm)' }}>
                <span style={{ color: '#ffff00' }}>$</span> {user ? 'cd /admin' : 'auth login --provider github'}
              </div>
              <a
                href={user ? '/admin' : '/handler/sign-in'}
                style={{
                  color: '#00ff00',
                  textDecoration: 'underline',
                  fontFamily: 'inherit',
                }}
              >
                {'>'} {user ? 'Navigate to admin dashboard →' : 'Initiate GitHub OAuth flow →'}
              </a>
            </div>

            {/* Footer */}
            <div style={{
              fontSize: '0.75rem',
              color: '#00ff00',
              opacity: 0.7,
              marginTop: 'var(--spacing-2xl)',
            }}>
              [Use theme switcher (bottom right ↘) to exit terminal mode]
            </div>
          </div>
        </Container>
      </div>
    )
  }

  // Mac OS Classic Layout - Retro window chrome and spatial metaphors
  if (themeConfig.id === 'mac-os-classic') {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#c0c0c0',
        backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(0,0,0,.05) 2px, rgba(0,0,0,.05) 4px)',
        fontFamily: 'Chicago, "Geneva", sans-serif',
        padding: 'var(--spacing-md)',
      }}>
        <Container size="lg">
          {/* Main Window */}
          <div style={{
            backgroundColor: '#fff',
            border: '2px solid #000',
            boxShadow: '4px 4px 0 rgba(0,0,0,0.5)',
            marginTop: 'var(--spacing-2xl)',
          }}>
            {/* Window Title Bar */}
            <div style={{
              backgroundColor: '#000',
              color: '#fff',
              padding: 'var(--spacing-sm) var(--spacing-md)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid #000',
            }}>
              <span style={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                📁 Agustin Musi Portfolio
              </span>
              <span style={{ fontSize: '0.75rem' }}>✕</span>
            </div>

            {/* Window Content */}
            <div style={{ padding: 'var(--spacing-xl)' }}>
              {/* Header */}
              <div style={{
                textAlign: 'center',
                marginBottom: 'var(--spacing-2xl)',
                paddingBottom: 'var(--spacing-lg)',
                borderBottom: '2px solid #000',
              }}>
                <h1 style={{
                  fontSize: 'clamp(1.5rem, 5vw, 2rem)',
                  fontWeight: 'bold',
                  marginBottom: 'var(--spacing-md)',
                }}>
                  Agustin Musi
                </h1>
                <p style={{
                  fontSize: 'clamp(0.875rem, 2.5vw, 1rem)',
                  marginBottom: 'var(--spacing-xs)',
                  padding: '0 var(--spacing-sm)',
                  fontWeight: 'bold',
                }}>
                  Habitusnet Consulting AG
                </p>
                <p style={{
                  fontSize: 'clamp(1rem, 3vw, 1.125rem)',
                  marginBottom: 'var(--spacing-sm)',
                  padding: '0 var(--spacing-sm)',
                }}>
                  {getHeadline()}
                </p>
                <p style={{
                  fontSize: 'clamp(0.8125rem, 2.5vw, 0.875rem)',
                  maxWidth: '600px',
                  margin: '0 auto',
                  lineHeight: 1.6,
                  padding: '0 var(--spacing-sm)',
                }}>
                  {getDescription()}
                </p>
              </div>

              {/* Career Info Boxes */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: 'var(--spacing-lg)',
                marginBottom: 'var(--spacing-2xl)',
              }}>
                {/* Career Highlights Box */}
                {(() => {
                  const teamsData = stats.find(s => s.slug === 'teams')
                  const metrics = teamsData?.entries || []
                  return (
                    <div style={{
                      border: '2px solid #000',
                      padding: 'var(--spacing-md)',
                      backgroundColor: '#fff',
                    }}>
                      <h3 style={{
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        marginBottom: 'var(--spacing-sm)',
                        borderBottom: '1px solid #000',
                        paddingBottom: 'var(--spacing-xs)',
                      }}>
                        Career Highlights
                      </h3>
                      <ul style={{
                        listStyle: 'none',
                        fontSize: '0.875rem',
                        lineHeight: 1.8,
                        paddingLeft: 0,
                      }}>
                        {metrics.slice(0, 4).map((m) => (
                          <li key={m.name} style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>• {m.name.replace('Total Team Members Managed', 'Team Members')}</span>
                            <span style={{ fontWeight: 'bold', color: m.color }}>{m.value}{m.suffix || ''}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )
                })()}

                {/* Current Role Box */}
                {(() => {
                  const currentRole = career[0]
                  return (
                    <div style={{
                      border: '2px solid #000',
                      padding: 'var(--spacing-md)',
                      backgroundColor: '#fff',
                    }}>
                      <h3 style={{
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        marginBottom: 'var(--spacing-sm)',
                        borderBottom: '1px solid #000',
                        paddingBottom: 'var(--spacing-xs)',
                      }}>
                        Current Role
                      </h3>
                      <div style={{ fontSize: '0.875rem', lineHeight: 1.6 }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                          {currentRole.role}
                        </div>
                        <div style={{ marginBottom: '0.5rem' }}>
                          {currentRole.company}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#666' }}>
                          {currentRole.location} • {currentRole.start} - {currentRole.end}
                        </div>
                      </div>
                    </div>
                  )
                })()}

                {/* Experience Timeline Box */}
                {(() => {
                  const experienceData = stats.find(s => s.slug === 'experience')
                  const entries = experienceData?.entries?.slice(0, 4) || []
                  return (
                    <div style={{
                      border: '2px solid #000',
                      padding: 'var(--spacing-md)',
                      backgroundColor: '#fff',
                    }}>
                      <h3 style={{
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        marginBottom: 'var(--spacing-sm)',
                        borderBottom: '1px solid #000',
                        paddingBottom: 'var(--spacing-xs)',
                      }}>
                        Experience
                      </h3>
                      <ul style={{
                        listStyle: 'none',
                        fontSize: '0.875rem',
                        lineHeight: 1.8,
                        paddingLeft: 0,
                      }}>
                        {entries.map((e) => {
                          const years = e.start && e.end ? e.end - e.start : 0
                          return (
                            <li key={e.name} style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>• {e.name}</span>
                              <span style={{ fontWeight: 'bold', color: e.color }}>{years} yrs</span>
                            </li>
                          )
                        })}
                      </ul>
                    </div>
                  )
                })()}
              </div>

              {/* Button */}
              <div style={{
                textAlign: 'center',
                padding: 'var(--spacing-lg)',
                border: '2px solid #000',
                backgroundColor: '#f0f0f0',
              }}>
                <p style={{
                  fontSize: '0.875rem',
                  marginBottom: 'var(--spacing-md)',
                }}>
                  {user ? `Welcome back, ${user.displayName || user.email}!` : 'Click to sign in with GitHub'}
                </p>
                <a
                  href={user ? '/admin' : '/handler/sign-in'}
                  style={{
                    display: 'inline-block',
                    padding: 'var(--spacing-sm) var(--spacing-lg)',
                    backgroundColor: '#000',
                    color: '#fff',
                    border: '2px solid #000',
                    fontSize: '0.875rem',
                    fontWeight: 'bold',
                    textDecoration: 'none',
                    boxShadow: '2px 2px 0 rgba(0,0,0,0.5)',
                  }}
                >
                  {user ? '→ Dashboard' : '→ Sign In'}
                </a>
              </div>
            </div>
          </div>
        </Container>
      </div>
    )
  }

  // Mac Studio Layout - macOS Sonoma-inspired with frosted glass
  if (themeConfig.id === 'mac-studio') {
    return (
      <MacStudioLayout>
        {/* Portfolio Finder Window - Career, Education, Certifications, Products */}
        <PortfolioFinderWindow
          windowId="portfolio-window"
          initialPosition={{ x: 20, y: 20 }}
        />

        {/* Hero Window */}
        <MacStudioWindow
          windowId="hero-window"
          title="Agustin Musi"
          size="large"
          initialPosition={{ x: 240, y: 20 }}
        >
          <div style={{ padding: 'var(--mac-spacing-lg)' }}>
            {/* Hero Content */}
            <div style={{ textAlign: 'center', marginBottom: 'var(--mac-spacing-xl)' }}>
              <h1 style={{
                fontSize: 'clamp(1.75rem, 5vw, 2.5rem)',
                fontWeight: 600,
                marginBottom: 'var(--mac-spacing-md)',
                background: 'linear-gradient(135deg, var(--mac-accent-primary), var(--mac-accent-secondary))',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>
                Agustin Musi
              </h1>
              <p style={{
                fontSize: 'clamp(0.875rem, 2.5vw, 1rem)',
                fontWeight: 500,
                marginBottom: 'var(--mac-spacing-sm)',
                color: 'var(--mac-text-secondary)',
              }}>
                Habitusnet Consulting AG
              </p>
              <p style={{
                fontSize: 'clamp(1rem, 3vw, 1.125rem)',
                marginBottom: 'var(--mac-spacing-md)',
                color: 'var(--mac-text-primary)',
              }}>
                {getHeadline()}
              </p>
              <p style={{
                fontSize: 'clamp(0.8125rem, 2.5vw, 0.875rem)',
                maxWidth: '600px',
                margin: '0 auto',
                lineHeight: 1.6,
                color: 'var(--mac-text-secondary)',
              }}>
                {getDescription()}
              </p>
            </div>

            {/* Career Highlights Cards */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 'var(--mac-spacing-md)',
              marginBottom: 'var(--mac-spacing-xl)',
            }}>
              {/* Key Metrics Card */}
              {(() => {
                const teamsData = stats.find(s => s.slug === 'teams')
                const metrics = teamsData?.entries || []
                return (
                  <div style={{
                    padding: 'var(--mac-spacing-md)',
                    background: 'var(--mac-window-bg)',
                    borderRadius: 'var(--mac-radius-md)',
                    border: '1px solid var(--mac-border-subtle)',
                  }}>
                    <h3 style={{
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      marginBottom: 'var(--mac-spacing-sm)',
                      color: 'var(--mac-text-primary)',
                    }}>
                      Career Highlights
                    </h3>
                    <ul style={{
                      listStyle: 'none',
                      padding: 0,
                      fontSize: '0.8125rem',
                      color: 'var(--mac-text-secondary)',
                      lineHeight: 1.8,
                    }}>
                      {metrics.slice(0, 4).map((m) => (
                        <li key={m.name} style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>{m.name.replace('Total Team Members Managed', 'Team Members')}</span>
                          <span style={{ fontWeight: 600, color: m.color }}>{m.value}{m.suffix || ''}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              })()}

              {/* Recent Role Card */}
              {(() => {
                const currentRole = career[0]
                return (
                  <div style={{
                    padding: 'var(--mac-spacing-md)',
                    background: 'var(--mac-window-bg)',
                    borderRadius: 'var(--mac-radius-md)',
                    border: '1px solid var(--mac-border-subtle)',
                  }}>
                    <h3 style={{
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      marginBottom: 'var(--mac-spacing-sm)',
                      color: 'var(--mac-text-primary)',
                    }}>
                      Current Role
                    </h3>
                    <div style={{
                      fontSize: '0.8125rem',
                      color: 'var(--mac-text-secondary)',
                      lineHeight: 1.6,
                    }}>
                      <div style={{ fontWeight: 600, color: 'var(--mac-text-primary)', marginBottom: '0.25rem' }}>
                        {currentRole.role}
                      </div>
                      <div style={{ marginBottom: '0.5rem' }}>
                        {currentRole.company}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--mac-text-tertiary)' }}>
                        {currentRole.location} • {currentRole.start} - {currentRole.end}
                      </div>
                    </div>
                  </div>
                )
              })()}
            </div>

            {/* CTA Section */}
            <div style={{
              textAlign: 'center',
              padding: 'var(--mac-spacing-lg)',
              background: 'var(--mac-bg-elevated)',
              borderRadius: 'var(--mac-radius-md)',
            }}>
              <p style={{
                fontSize: '0.875rem',
                marginBottom: 'var(--mac-spacing-md)',
                color: 'var(--mac-text-secondary)',
              }}>
                {user ? `Welcome back, ${user.displayName || user.email}!` : 'Sign in to explore the full experience'}
              </p>
              <a
                href={user ? '/admin' : '/handler/sign-in'}
                style={{
                  display: 'inline-block',
                  padding: 'var(--mac-spacing-sm) var(--mac-spacing-lg)',
                  background: 'linear-gradient(135deg, var(--mac-accent-primary), var(--mac-accent-secondary))',
                  color: '#fff',
                  borderRadius: 'var(--mac-radius-md)',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  textDecoration: 'none',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                }}
              >
                {user ? 'Go to Dashboard' : 'Sign In with GitHub'}
              </a>
            </div>
          </div>
        </MacStudioWindow>

        {/* Tiered Content Demo */}
        <MacStudioWindow
          windowId="gated-content-window"
          title="Gated Content Preview"
          size="medium"
          initialPosition={{ x: 250, y: 450 }}
        >
          <div style={{ padding: 'var(--mac-spacing-md)' }}>
            <TieredContent
              requiredTier="registered"
              publicPreview={
                <div style={{ padding: 'var(--mac-spacing-lg)', textAlign: 'center' }}>
                  <p style={{ fontSize: '0.875rem', color: 'var(--mac-text-secondary)' }}>
                    Premium content preview - sign in to unlock full access to detailed project breakdowns,
                    video interviews, and exclusive case studies.
                  </p>
                </div>
              }
              accessMessage="Register to unlock detailed project breakdowns, video interviews, and exclusive content."
              onRequestAccess={() => {
                window.location.href = '/handler/sign-in'
              }}
            >
              <div style={{ padding: 'var(--mac-spacing-lg)' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 'var(--mac-spacing-md)' }}>
                  Full Access Content
                </h3>
                <p style={{ fontSize: '0.875rem', color: 'var(--mac-text-secondary)' }}>
                  This content is visible to registered users. Here you'd find detailed project case studies,
                  technical deep-dives, and exclusive video content.
                </p>
              </div>
            </TieredContent>
          </div>
        </MacStudioWindow>

        {/* Stats Finder Window - Locations, Experience, Companies, Teams */}
        <StatsWindow
          windowId="stats-window"
          initialPosition={{ x: 20, y: 380 }}
        />

        {/* Tech Stack Finder Window - By Role (CTO, Developer, etc.) */}
        <TechStackWindow
          windowId="tech-window"
          initialPosition={{ x: 20, y: 520 }}
        />
      </MacStudioLayout>
    )
  }

  // Executive Dashboard Layout - Data-dense with metrics
  if (themeConfig.id === 'executive-dashboard') {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#0a0e1a',
        color: '#e4e7eb',
        fontFamily: 'var(--font-sans)',
      }}>
        <Container size="full">
          {/* Dashboard Header */}
          <div style={{
            backgroundColor: '#111827',
            borderBottom: '1px solid #1f2937',
            padding: 'var(--spacing-md)',
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--spacing-md)',
            }}>
              <div>
                <h1 style={{ fontSize: 'clamp(1.25rem, 4vw, 1.5rem)', fontWeight: 600, marginBottom: '0.25rem' }}>
                  Agustin Musi - Habitusnet Consulting AG
                </h1>
                <p style={{ fontSize: 'clamp(0.75rem, 2vw, 0.875rem)', color: '#9ca3af' }}>
                  {getHeadline()}
                </p>
              </div>
              <div style={{
                display: 'flex',
                gap: 'var(--spacing-md)',
                fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
              }}>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ color: '#9ca3af' }}>Status</div>
                  <div style={{ color: '#10b981', fontWeight: 600 }}>● LIVE</div>
                </div>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ color: '#9ca3af' }}>User</div>
                  <div style={{ fontWeight: 600 }}>{user ? user.displayName || 'Authenticated' : 'Guest'}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Key Metrics Grid */}
          <div style={{
            padding: 'var(--spacing-xl)',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 'var(--spacing-md)',
          }}>
            {/* Cost Optimization Metric */}
            <div style={{
              backgroundColor: '#111827',
              padding: 'var(--spacing-lg)',
              borderLeft: '3px solid #10b981',
            }}>
              <div style={{ fontSize: '0.875rem', color: '#9ca3af', marginBottom: 'var(--spacing-xs)' }}>
                Cost Optimization
              </div>
              <div style={{ fontSize: '2.5rem', fontWeight: 700, color: '#10b981', marginBottom: 'var(--spacing-xs)' }}>
                83%
              </div>
              <div style={{ fontSize: '0.875rem', color: '#9ca3af' }}>
                $300/year savings (v1 $30/mo → v2 $5/mo)
              </div>
            </div>

            {/* Architecture Layers */}
            <div style={{
              backgroundColor: '#111827',
              padding: 'var(--spacing-lg)',
              borderLeft: '3px solid #3b82f6',
            }}>
              <div style={{ fontSize: '0.875rem', color: '#9ca3af', marginBottom: 'var(--spacing-xs)' }}>
                Architecture Layers
              </div>
              <div style={{ fontSize: '2.5rem', fontWeight: 700, color: '#3b82f6', marginBottom: 'var(--spacing-xs)' }}>
                4
              </div>
              <div style={{ fontSize: '0.875rem', color: '#9ca3af' }}>
                Orthogonal semantic separation
              </div>
            </div>

            {/* Tech Stack Coverage */}
            <div style={{
              backgroundColor: '#111827',
              padding: 'var(--spacing-lg)',
              borderLeft: '3px solid #f59e0b',
            }}>
              <div style={{ fontSize: '0.875rem', color: '#9ca3af', marginBottom: 'var(--spacing-xs)' }}>
                Modern Stack
              </div>
              <div style={{ fontSize: '2.5rem', fontWeight: 700, color: '#f59e0b', marginBottom: 'var(--spacing-xs)' }}>
                100%
              </div>
              <div style={{ fontSize: '0.875rem', color: '#9ca3af' }}>
                React 19, Neon, Cloudflare Workers
              </div>
            </div>

            {/* Current Configuration */}
            <div style={{
              backgroundColor: '#111827',
              padding: 'var(--spacing-lg)',
              borderLeft: '3px solid #8b5cf6',
            }}>
              <div style={{ fontSize: '0.875rem', color: '#9ca3af', marginBottom: 'var(--spacing-xs)' }}>
                Active Configuration
              </div>
              <div style={{ fontSize: '0.875rem', lineHeight: 1.8, marginTop: 'var(--spacing-md)' }}>
                <div><strong>Layout:</strong> {themeConfig.name}</div>
                <div><strong>Tokens:</strong> {tokenConfig.culturalOrigin}</div>
                <div><strong>View:</strong> {currentProfile}</div>
                <div><strong>Role:</strong> {currentPersona}</div>
              </div>
            </div>
          </div>

          {/* Detailed Metrics Table */}
          <div style={{
            margin: '0 var(--spacing-md) var(--spacing-md)',
            backgroundColor: '#111827',
            borderRadius: 'var(--radius-md)',
          }}>
            <div style={{
              padding: 'var(--spacing-md)',
              borderBottom: '1px solid #1f2937',
            }}>
              <h2 style={{ fontSize: 'clamp(1rem, 3vw, 1.25rem)', fontWeight: 600 }}>
                Platform Overview
              </h2>
              <p style={{ fontSize: 'clamp(0.75rem, 2vw, 0.875rem)', color: '#9ca3af', marginTop: 'var(--spacing-xs)' }}>
                {getDescription()}
              </p>
            </div>

            <div style={{ padding: 'var(--spacing-md)', overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                minWidth: '500px',
                fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                borderCollapse: 'collapse',
              }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #1f2937' }}>
                    <th style={{ textAlign: 'left', padding: 'var(--spacing-sm)', color: '#9ca3af' }}>Component</th>
                    <th style={{ textAlign: 'left', padding: 'var(--spacing-sm)', color: '#9ca3af' }}>Technology</th>
                    <th style={{ textAlign: 'left', padding: 'var(--spacing-sm)', color: '#9ca3af' }}>Status</th>
                    <th style={{ textAlign: 'right', padding: 'var(--spacing-sm)', color: '#9ca3af' }}>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #1f2937' }}>
                    <td style={{ padding: 'var(--spacing-sm)' }}>Frontend</td>
                    <td style={{ padding: 'var(--spacing-sm)' }}>React 19 + Vite 6</td>
                    <td style={{ padding: 'var(--spacing-sm)', color: '#10b981' }}>✓ Active</td>
                    <td style={{ padding: 'var(--spacing-sm)', textAlign: 'right' }}>$0</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #1f2937' }}>
                    <td style={{ padding: 'var(--spacing-sm)' }}>Database</td>
                    <td style={{ padding: 'var(--spacing-sm)' }}>Neon PostgreSQL 17</td>
                    <td style={{ padding: 'var(--spacing-sm)', color: '#10b981' }}>✓ Active</td>
                    <td style={{ padding: 'var(--spacing-sm)', textAlign: 'right' }}>$0</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #1f2937' }}>
                    <td style={{ padding: 'var(--spacing-sm)' }}>Auth</td>
                    <td style={{ padding: 'var(--spacing-sm)' }}>Stack Auth + GitHub</td>
                    <td style={{ padding: 'var(--spacing-sm)', color: '#10b981' }}>✓ Active</td>
                    <td style={{ padding: 'var(--spacing-sm)', textAlign: 'right' }}>$0</td>
                  </tr>
                  <tr>
                    <td style={{ padding: 'var(--spacing-sm)' }}>Hosting</td>
                    <td style={{ padding: 'var(--spacing-sm)' }}>Cloudflare Workers</td>
                    <td style={{ padding: 'var(--spacing-sm)', color: '#10b981' }}>✓ Active</td>
                    <td style={{ padding: 'var(--spacing-sm)', textAlign: 'right' }}>$5/mo</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Action Button */}
            <div style={{
              padding: 'var(--spacing-lg)',
              borderTop: '1px solid #1f2937',
              textAlign: 'center',
            }}>
              <a
                href={user ? '/admin' : '/handler/sign-in'}
                style={{
                  display: 'inline-block',
                  padding: 'var(--spacing-sm) var(--spacing-xl)',
                  backgroundColor: '#3b82f6',
                  color: '#fff',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  textDecoration: 'none',
                  transition: 'background-color 0.2s',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#2563eb'
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '#3b82f6'
                }}
              >
                {user ? 'Access Dashboard →' : 'Authenticate with GitHub →'}
              </a>
            </div>
          </div>
        </Container>
      </div>
    )
  }

  // Fallback for unknown layout themes
  return <div>Layout theme '{themeConfig.id}' not yet implemented. Please switch to 'modern-cards'.</div>
}
